import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import { useModal } from 'hooks/useModal';
import { MorphologyType } from 'types/ProductPageV2/Products';

import 'react-image-gallery/styles/scss/image-gallery.scss';
import styles from './ProductGallery.module.scss';

type Props = {
  /** List of images and videos (main images/videos and their thumbnails) */
  data: Array<
    ReactImageGalleryItem & { morphology: MorphologyType; isVideo: boolean }
  >;
  selectedMorphologyType: Dispatch<SetStateAction<MorphologyType>>;
  disableZoomMode?: boolean;
  onSlideAction?: (direction: 'prev' | 'next', e?: SyntheticEvent) => void;
};

/**
 * ProductGallery for mobile and portrait tablet
 */
export default function ProductGalleryMobile({
  data,
  selectedMorphologyType,
  onSlideAction,
  disableZoomMode = false,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const [galeryDomItems, setGaleryDomItems] = useState<HTMLDivElement[]>([]);
  const imageGalleryRef = useRef<
    ImageGallery & { imageGallery: MutableRefObject<HTMLDivElement> }
  >(null);

  const { disableBodyScroll, enableBodyScroll } = useModal();
  const cloneData = useMemo(() => {
    const cloneData = [...data];

    // Duplicate for don't show the transition from left to right
    if (cloneData.length > 1 && cloneData.length <= 3) {
      cloneData.push(...cloneData);
    }

    return cloneData;
  }, [data]);

  function renderFullscreenButton(
    onClick: React.MouseEventHandler<HTMLElement>,
    isFullscreen: boolean
  ) {
    return (
      <>
        {isFullscreen && (
          <button type="button" className={styles.close} onClick={onClick} />
        )}
      </>
    );
  }

  function renderRightNav(onClick: React.MouseEventHandler<HTMLElement>) {
    return (
      <button
        onClick={onClick}
        className={styles.rightNav}
        aria-label={t('LABEL_NEXT')}
      />
    );
  }

  function playVideo(index: number) {
    galeryDomItems[index]?.getElementsByTagName('video')[0]?.play();
  }

  function stopVideo(index: number) {
    const video = galeryDomItems[index]?.getElementsByTagName('video')[0];
    if (video) {
      video.currentTime = 0;
      video.pause();
    }
  }

  function onSlide(index: number) {
    cloneData[index + 1]?.isVideo && stopVideo(index + 1);
    cloneData[index - 1]?.isVideo && stopVideo(index - 1);
    cloneData[index].isVideo && playVideo(index);
    selectedMorphologyType(cloneData[index]?.morphology);
  }

  useEffect(() => {
    const items =
      imageGalleryRef.current?.imageGallery?.current?.querySelectorAll(
        '.image-gallery-slide'
      ) || [];

    setGaleryDomItems(items as HTMLDivElement[]);
    onSlide(0);
  }, [cloneData]);

  useEffect(() => {
    return () => {
      enableBodyScroll();
    };
  }, []);

  return (
    <ImageGallery
      additionalClass={cloneData.length === 1 ? 'oneItemGallery' : ''}
      ref={imageGalleryRef}
      items={cloneData}
      showThumbnails={false}
      showPlayButton={false}
      showBullets={false}
      renderRightNav={renderRightNav}
      renderLeftNav={() => null}
      renderFullscreenButton={renderFullscreenButton}
      onScreenChange={isFullScreen => {
        isFullScreen ? disableBodyScroll() : enableBodyScroll();
      }}
      useBrowserFullscreen={false}
      onBeforeSlide={nextIndex => {
        const currentIndex = imageGalleryRef.current?.getCurrentIndex() as number;

        onSlideAction?.(nextIndex > currentIndex ? 'next' : 'prev');
      }}
      onClick={e => {
        if (disableZoomMode) return;

        const el = (e.target as Element).closest('.image-gallery-slide');

        if (el?.classList.contains('center')) {
          imageGalleryRef?.current?.fullScreen();
        }
      }}
      onSlide={onSlide}
    />
  );
}
